import { useState, useEffect, useRef } from 'react';
import { showToast } from '../utils/toast';

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState<'good' | 'fair' | 'poor' | 'unknown'>('unknown');
  const [lastOnlineTime, setLastOnlineTime] = useState<Date | null>(navigator.onLine ? new Date() : null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  
  // Use a ref to track toast IDs for updating
  const toastIdRef = useRef<string | null>(null);
  
  // Check connection quality using Navigation API if available
  useEffect(() => {
    const checkConnectionQuality = () => {
      if ('connection' in navigator && navigator.connection) {
        const connection = navigator.connection as any;
        
        if (connection.effectiveType) {
          switch (connection.effectiveType) {
            case '4g':
              setConnectionQuality('good');
              break;
            case '3g':
              setConnectionQuality('fair');
              break;
            case '2g':
            case 'slow-2g':
              setConnectionQuality('poor');
              break;
            default:
              setConnectionQuality('unknown');
          }
        }
        
        // Listen for connection changes
        if (connection.addEventListener) {
          connection.addEventListener('change', checkConnectionQuality);
          return () => connection.removeEventListener('change', checkConnectionQuality);
        }
      }
    };
    
    checkConnectionQuality();
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setLastOnlineTime(new Date());
      setReconnectAttempts(0);
      
      if (wasOffline) {
        showToast.network.online();
        setWasOffline(false);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      
      // Show toast with reconnection info
      toastIdRef.current = showToast.network.offline();
    };
    
    // Simulate reconnection attempts when offline
    let reconnectInterval: NodeJS.Timeout | null = null;
    
    if (!isOnline) {
      reconnectInterval = setInterval(() => {
        setReconnectAttempts(prev => {
          const newCount = prev + 1;
          
          // Update toast with reconnection attempts
          if (toastIdRef.current) {
            toast.loading(`Attempting to reconnect... (Attempt ${newCount})`, { id: toastIdRef.current });
          }
          
          return newCount;
        });
        
        // Check if we're back online
        if (navigator.onLine) {
          handleOnline();
          if (reconnectInterval) clearInterval(reconnectInterval);
        }
      }, 5000); // Try every 5 seconds
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (reconnectInterval) clearInterval(reconnectInterval);
    };
  }, [wasOffline, isOnline]);
  
  // Monitor for slow responses
  useEffect(() => {
    if (!isOnline) return;
    
    const checkResponseTime = async () => {
      try {
        const startTime = Date.now();
        const response = await fetch('/api/ping', { 
          method: 'HEAD',
          cache: 'no-cache',
          headers: { 'Cache-Control': 'no-cache' }
        });
        const endTime = Date.now();
        const responseTime = endTime - startTime;
        
        // Classify connection quality based on response time
        if (responseTime < 300) {
          setConnectionQuality('good');
        } else if (responseTime < 1000) {
          setConnectionQuality('fair');
        } else {
          setConnectionQuality('poor');
          showToast.network.slowConnection();
        }
      } catch (error) {
        // Failed to fetch, but navigator.onLine still says we're online
        // This could be a server issue or partial connectivity
        if (navigator.onLine) {
          setConnectionQuality('poor');
          showToast.network.connectionError();
        }
      }
    };
    
    // Check response time periodically
    const interval = setInterval(checkResponseTime, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, [isOnline]);

  // Calculate time since last online
  const getTimeSinceLastOnline = () => {
    if (!lastOnlineTime || isOnline) return null;
    
    const now = new Date();
    const diffMs = now.getTime() - lastOnlineTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMins > 0) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else {
      return 'just now';
    }
  };

  return { 
    isOnline, 
    wasOffline,
    connectionQuality,
    lastOnlineTime,
    timeSinceLastOnline: getTimeSinceLastOnline(),
    reconnectAttempts
  };
}